import { useCallback, useMemo, useRef } from 'react';
import useResizeObserverHeight from 'hooks/useResizeObserverHeight';
import { TextareaAutosize, Tooltip } from '@mui/material';
import ReactQuill from 'react-quill';
import IconCopy from 'crono-fe-common/icons/Icon-Copy';
import { CronoEditorRichField } from 'components/CronoEditor/CronoEditorRichField';
import { ExternalPropertyNotTagFooter } from './ExternalPropertyNotTagFooter';
import { NotTagPropertyWrapper } from './style';
import { useSnackbar } from 'context/snackbar';
import { cn } from 'utils/style-utils';
import { CronoEditorRichFieldModal } from 'components/CronoEditor/CronoEditorRichFieldModal';
import { useCommittableDraft } from 'hooks/useCommittableDraft';

type Props = {
  value: string;
  placeholder: string;
  label?: string;
  tooltipMessage?: string;
  renderHtml: boolean;
  disabled?: boolean;

  minRows?: number;
  maxRows?: number;
  height?: string;

  onHeightChange?: (height: number) => void;
  save: (nextValue: string) => void;
};

export const ExternalPropertyTextInput = ({
  value,
  disabled,
  placeholder,
  tooltipMessage,
  renderHtml,
  label,
  minRows,
  maxRows,
  height,
  onHeightChange,
  save,
}: Props) => {
  const editorRef = useRef<ReactQuill>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { setDraft, commitDraft } = useCommittableDraft(value, save);

  useResizeObserverHeight(containerRef, onHeightChange);
  const { pushSnackbar } = useSnackbar();

  const isValuePresent = useMemo(() => !!value, [value]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value ?? '');
      pushSnackbar({ message: 'Copied to clipboard', severity: 'success' });
    } catch (e) {
      console.error('Failed to copy to clipboard', e);
      pushSnackbar({
        message: 'Failed to copy to clipboard',
        severity: 'error',
      });
    }
  }, [pushSnackbar, value]);

  const actions = useMemo(
    () => [
      {
        key: 'expand',
        tooltip: 'Expand',
        element: () => (
          <CronoEditorRichFieldModal
            initialValue={value ?? ''}
            onSave={(v) => commitDraft(v)}
            placeholder={placeholder}
            label={label ?? ''}
            type={renderHtml ? 'html' : 'text'}
          />
        ),
      },
      {
        key: 'copy',
        tooltip: 'Copy',
        handle: handleCopy,
        icon: <IconCopy size={18} color="currentColor" />,
        hidden: !isValuePresent,
      },
    ],
    [
      handleCopy,
      isValuePresent,
      label,
      commitDraft,
      placeholder,
      renderHtml,
      value,
    ],
  );

  return (
    <Tooltip arrow title={tooltipMessage ?? ''} placement="bottom">
      <div>
        {renderHtml ? (
          <CronoEditorRichField
            ref={containerRef}
            editorRef={editorRef}
            readOnly={disabled}
            placeholder={placeholder}
            defaultValue={value || ''}
            onUserValueChange={setDraft}
            onBlur={(v) => commitDraft(v)}
            onSave={(v) => commitDraft(v)}
            minRows={minRows}
            containerClassName="resize-y overflow-y-auto"
            containerStyle={{
              ...(height ? { height } : {}),
              minHeight: '60px',
            }}
            label={label}
          >
            {() => <ExternalPropertyNotTagFooter actions={actions} />}
          </CronoEditorRichField>
        ) : (
          <NotTagPropertyWrapper
            ref={containerRef}
            className={'ai-variable-container'}
            style={{ height: height }}
          >
            <TextareaAutosize
              key={value}
              className={cn(
                'text-area-no-border',
                disabled && 'field-disabled',
              )}
              minRows={minRows}
              maxRows={maxRows}
              disabled={disabled}
              placeholder={placeholder}
              defaultValue={value || ''}
              onChange={(ev) => setDraft(ev.target.value)}
              onBlur={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                commitDraft(ev.target.value);
              }}
              style={{ height: 100, overflow: height ? 'auto' : 'hidden' }}
            />

            <ExternalPropertyNotTagFooter actions={actions} />
          </NotTagPropertyWrapper>
        )}
      </div>
    </Tooltip>
  );
};
